import { Button, Spin } from "antd";
import { DateUtils } from "utils/dateutils";
import { Link } from "react-router-dom";
import { ScheduleCiqIcon, SubmittalCiqIcon } from "components/svg-icons";
import { TruncatedTextWithTooltip } from "components/cell-renders";
import { TOUR_TARGETS } from "components/tour";
import {
  DashboardAlerts,
  FilterNamesEnum,
  NavigationPageEnum
} from "../../models";

function AlertMessageItem({
  title,
  linkButton,
  buttonData,
  text,
  alertOn
}: {
  title: string;
  buttonData?: any;
  text: any;
  alertOn?: string;
  linkButton?: any;
}) {
  return (
    <div className="p-2 flex flex-col space-y-2 w-full items-center box-border">
      <div className="flex justify-between items-center w-full px-2">
        <div className="box-header !text-sm">{title}</div>
        {linkButton}
      </div>

      <div className="flex justify-center">{buttonData}</div>
      {text}
      {alertOn && (
        <div className="text-black text-opacity-60 italic">{alertOn}</div>
      )}
    </div>
  );
}

function AlertDashboard({
  alerts,
  activities_modified,
  projectId,
  goToFiltered,
  lastSyncDateTime
}: {
  alerts: DashboardAlerts | undefined;
  activities_modified: number | undefined;
  projectId: string;
  goToFiltered: (
    page: NavigationPageEnum,
    filter: FilterNamesEnum,
    value?: any
  ) => void;
  lastSyncDateTime: string | undefined;
}) {
  return (
    <div className="box-border w-full" data-tour={TOUR_TARGETS.ALERTS}>
      <div className="box-header bottom-border !p-6">Alerts</div>

      {!alerts ? (
        <div className="flex w-full h-40 justify-center items-center">
          <Spin />
        </div>
      ) : (
        <div className="flex flex-col space-y-6 p-6 w-full h-full">
          {!alerts.schedule_version?.import_log_id &&
            !alerts.activities_modified_date &&
            (alerts.new_submittals?.count === undefined ||
              alerts.new_submittals?.count === 0) &&
            (alerts?.submittals_not_found_in_source?.count === undefined ||
              alerts?.submittals_not_found_in_source?.count === 0) && (
              <div className="flex items-center p-2 justify-center text-xs h-40">
                <div>There are no new alerts for today.</div>
              </div>
            )}

          {alerts.schedule_version?.import_log_id && (
            <AlertMessageItem
              title="New Schedule Available"
              buttonData={
                <div className="flex items-center space-x-1 border border-solid border-ciq-orange-400 px-2 py-0.5 bg-ciq-orange-400 bg-opacity-30 rounded-sm">
                  <ScheduleCiqIcon />
                  <TruncatedTextWithTooltip
                    text={`${alerts.schedule_version.schedule_date || ""}-${
                      alerts.schedule_version.schedule_name || ""
                    }`}
                    disableTooltip={false}
                    className="max-w-xs text-xs"
                  />
                </div>
              }
              linkButton={
                <Link
                  to={`/project/${projectId}/schedule/project-schedule/${alerts.schedule_version?.import_log_id}`}
                  data-testid="view-schedule-link"
                >
                  View
                </Link>
              }
              text={
                (activities_modified ?? 0) > 0 && (
                  <div className="flex space-x-1">
                    <div className="font-bold">{activities_modified}</div>
                    <div>linked Schedule activities have been affected.</div>
                  </div>
                )
              }
              alertOn={`Alerted on: ${DateUtils.format(
                alerts.schedule_version.created_at
              )}`}
              data-testid="new-schedule-alert"
            />
          )}

          {(alerts?.submittals_not_found_in_source?.count ?? 0) > 0 && (
            <AlertMessageItem
              title="Missing Submittals"
              buttonData={
                <div className="flex items-center space-x-1 border border-solid border-ciq-primary-900-light px-2 py-1 bg-ciq-primary-900-light bg-opacity-30 rounded-sm">
                  <SubmittalCiqIcon size={14} />
                  <div className="max-w-xs min-w-[20px]">
                    {alerts.submittals_not_found_in_source.count}
                  </div>
                </div>
              }
              linkButton={
                <Button
                  type="link"
                  className="p-0 h-fit"
                  onClick={() => {
                    goToFiltered(
                      NavigationPageEnum.SubmittalLog,
                      FilterNamesEnum.SubmittalsNotFoundInSource,
                      true
                    );
                  }}
                  data-testid="view-submittals-not-found-in-sync-button"
                >
                  View
                </Button>
              }
              text={<div>Submittals are missing in the latest sync.</div>}
              alertOn={`Alerted on: ${
                lastSyncDateTime ? DateUtils.format(lastSyncDateTime) : ""
              }`}
              data-testid="submittals-not-found-in-sync-alert"
            />
          )}

          {alerts.schedule_version?.import_log_id &&
            alerts.activities_modified_date &&
            activities_modified === 0 && (
              <AlertMessageItem
                title="Version Update"
                text={
                  <div className="px-4">
                    No activities related to the submittals and/or materials
                    have been modified.
                  </div>
                }
                data-testid="version-update-alert"
              />
            )}
        </div>
      )}
    </div>
  );
}
export default AlertDashboard;
